import React, { useState } from 'react'

import { Mutation } from '@apollo/client/react/components'
import { Icon, List, Switch } from 'antd'
import gql from 'graphql-tag'
import _ from 'lodash'
import PT from 'prop-types'
import styled from 'styled-components'

import errorService from '../../../../../../../../../utils/analytics/error'
import feedback from '../../../../../../../../../utils/feedback'
import ConfirmModal from '../../../../../ConfirmModal'

const ActionContainer = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`

const Container = styled(/** @type {any} */ (List.Item))`
  border-bottom: 1px solid #e5e5e5;
  border-radius: 4px;
  padding: 8px 24px 8px 24px !important;
  width: 100%;
`

const disableUniversityClientUserAccessMutation = gql`
  mutation DisableUniversityClientUserAccess($data: DisableUniversityClientUserAccessInput!) {
    disableUniversityClientUserAccess(data: $data) {
      id
      accessDenied
    }
  }
`

const enableUniversityClientUserAccessMutation = gql`
  mutation EnableUniversityClientUserAccess($data: EnableUniversityClientUserAccessInput!) {
    enableUniversityClientUserAccess(data: $data) {
      id
      accessDenied
    }
  }
`

const disableUniversityClientUserAdminAccessMutation = gql`
  mutation DisableUniversityClientUserAdminAccess($data: DisableUniversityClientUserAccessInput!) {
    disableUniversityClientUserAdminAccess(data: $data) {
      id
      isAdmin
    }
  }
`

const enableUniversityClientUserAdminAccessMutation = gql`
  mutation EnableUniversityClientUserAdminAccess($data: EnableUniversityClientUserAccessInput!) {
    enableUniversityClientUserAdminAccess(data: $data) {
      id
      isAdmin
    }
  }
`

const deleteUserMutation = gql`
  mutation deleteUniversityClientUser($userIdToDelete: ID!) {
    deleteUniversityClientUser(userIdToDelete: $userIdToDelete)
  }
`

const MemberListItem = props => {
  const { accessDenied, email, fullName, id, isAdmin, refetchMembers } = props

  const [deleteModalVisible, setDeleteModalVisible] = useState(false)

  const onChange = (enableAccess, disableAccess) => async value => {
    try {
      const payload = { variables: { data: { id } } }
      if (value) {
        await enableAccess(payload)
      } else {
        await disableAccess(payload)
      }
      feedback.success({ content: 'Access updated successfully' })
    } catch (error) {
      errorService.report(error, 'update access')
      feedback.error({
        content: error.message,
        title: 'Error updating access',
      })
    }
  }

  const memoizedOnChange = _.memoize(onChange)

  const onAdminAccessChange = (enableAdminAccess, disableAdminAccess) => async value => {
    try {
      const payload = { variables: { data: { id } } }
      if (value) {
        await enableAdminAccess(payload)
      } else {
        await disableAdminAccess(payload)
      }
      refetchMembers()
      feedback.success({ content: 'Admin access updated successfully' })
    } catch (error) {
      errorService.report(error, 'update admin access')
      feedback.error({
        content: error.message,
        title: 'Error updating admin access',
      })
    }
  }

  const onDeleteUser = async deleteUser => {
    try {
      const payload = { variables: { userIdToDelete: id } }
      await deleteUser(payload)
      refetchMembers()
      feedback.success({ content: 'User deleted successfully' })
    } catch (error) {
      errorService.report(error, 'delete user')
      feedback.error({
        content: error.message,
        title: 'Error deleting user',
      })
    }
  }

  const memoizedOnAdminAccessChange = _.memoize(onAdminAccessChange)

  return (
    <Container>
      <div className="ant-list-item-meta">
        <div className="ant-list-item-meta-content">
          <div className="ant-list-item-meta-title">{fullName}</div>
          <div className="ant-list-item-meta-description">{email}</div>
        </div>
      </div>
      <ActionContainer style={{ marginRight: 55 }}>
        <Mutation mutation={disableUniversityClientUserAdminAccessMutation}>
          {disableAdminAccess => (
            <Mutation mutation={enableUniversityClientUserAdminAccessMutation}>
              {enableAdminAccess => (
                <Switch
                  aria-label={`${fullName} is admin`}
                  defaultChecked={isAdmin}
                  onChange={memoizedOnAdminAccessChange(enableAdminAccess, disableAdminAccess)}
                />
              )}
            </Mutation>
          )}
        </Mutation>
      </ActionContainer>
      <ActionContainer style={{ marginRight: 30 }}>
        <Mutation mutation={disableUniversityClientUserAccessMutation}>
          {disableAccess => (
            <Mutation mutation={enableUniversityClientUserAccessMutation}>
              {enableAccess => (
                <Switch
                  aria-label={`${fullName} is enabled`}
                  disabled={isAdmin}
                  defaultChecked={!accessDenied}
                  onChange={memoizedOnChange(enableAccess, disableAccess)}
                />
              )}
            </Mutation>
          )}
        </Mutation>
      </ActionContainer>
      <ActionContainer>
        <Mutation mutation={deleteUserMutation}>
          {deleteUser => (
            <>
              <button
                aria-label={`Delete member ${fullName}`}
                onClick={() => setDeleteModalVisible(true)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                type="button"
              >
                <Icon aria-hidden="true" style={{ fontSize: 20 }} theme="twoTone" type="delete" />
              </button>
              <ConfirmModal
                onCancel={() => setDeleteModalVisible(false)}
                onConfirm={() => {
                  onDeleteUser(deleteUser)
                  setDeleteModalVisible(false)
                }}
                title="Are you sure you want to delete this user?"
                visible={deleteModalVisible}
              />
            </>
          )}
        </Mutation>
      </ActionContainer>
    </Container>
  )
}

MemberListItem.propTypes = {
  accessDenied: PT.bool.isRequired,
  email: PT.string.isRequired,
  fullName: PT.string.isRequired,
}

export default MemberListItem
